import React, { useState, useContext } from 'react';
import Header from '../../components/Header/Header';
import useTranslation from '../../hooks/useTranslation';
import { TranslationContext } from '../../helpers/Context';
import './help.css';

const Help = () => {
  const T = useTranslation();
  const { language } = useContext(TranslationContext);
  const [activeTab, setActiveTab] = useState('acronyms');

  // Datos de los acrónimos organizados por categorías
  const acronymsData = {
    rhythm: [
      { acronym: 'AESP', meaning: 'AESP_meaning' },
      { acronym: 'FV', meaning: 'FV_meaning' },
      { acronym: 'TSV', meaning: 'TSV_meaning' },
      { acronym: 'TVCP', meaning: 'TVCP_meaning' },
      { acronym: 'TVSP', meaning: 'TVSP_meaning' },
      { acronym: 'Sinusal', meaning: 'Sinusal_meaning' },
    ],
    blocks: [
      { acronym: 'Bloq. 1º', meaning: 'Bloq1_meaning' },
      { acronym: 'Bloq. 2º I', meaning: 'Bloq2I_meaning' },
      { acronym: 'Bloq. 2º II', meaning: 'Bloq2II_meaning' },
      { acronym: 'Bloq. 3º', meaning: 'Bloq3_meaning' },
    ],
    procedures: [
      { acronym: 'PCR', meaning: 'PCR_meaning' },
      { acronym: 'RCP', meaning: 'RCP_meaning' },
      { acronym: 'RCE', meaning: 'RCE_meaning' },
      { acronym: 'IOT', meaning: 'IOT_meaning' },
      { acronym: 'IOT/DSG', meaning: 'IOT_DSG_meaning' },
      { acronym: 'DSG', meaning: 'DSG_meaning' },
      { acronym: 'DEA', meaning: 'DEA_meaning' },
      { acronym: 'ECG', meaning: 'ECG_meaning' },
      { acronym: 'OVACE', meaning: 'OVACE_meaning' },
    ],
  };

  // Datos de los botones organizados por categorías
  const buttonsData = {
    main: [
      { button: 'start_case_button', description: 'start_case_description', icon: '▶' },
      { button: 'stop_case_button', description: 'stop_case_description', icon: '⏹' },
      { button: 'start_pcr_button', description: 'start_pcr_description', icon: '🫀' },
      { button: 'rce_button', description: 'rce_description', icon: '🔄' },
    ],
    rhythm: [
      { button: 'patient_rhythm_button', description: 'patient_rhythm_description', icon: '📊' },
      { button: 'evaluate_rhythm_button', description: 'evaluate_rhythm_description', icon: '🔍' },
    ],
    cpr: [
      { button: 'start_compressions_button', description: 'start_compressions_description', icon: '👐' },
      { button: 'stop_compressions_button', description: 'stop_compressions_description', icon: '✋' },
      { button: 'start_ventilations_button', description: 'start_ventilations_description', icon: '🫁' },
      { button: 'stop_ventilations_button', description: 'stop_ventilations_description', icon: '🛑' },
    ],
    treatment: [
      { button: 'defibrillation_button', description: 'defibrillation_description', icon: '⚡' },
      { button: 'adrenaline_button', description: 'adrenaline_description', icon: '💉' },
      { button: 'amiodarone_button', description: 'amiodarone_description', icon: '💊' },
    ],
    airway: [
      { button: 'vascular_access_button', description: 'vascular_access_description', icon: '💉' },
      { button: 'iot_dsg_button', description: 'iot_dsg_description', icon: '🦷' },
    ],
    quality: [
      { button: 'quality_button', description: 'quality_description', icon: '⭐' },
      { button: 'non_technical_button', description: 'non_technical_description', icon: '👥' },
    ],
    other: [
      { button: 'save_report_button', description: 'save_report_description', icon: '📄' },
      { button: 'emergency_team_button', description: 'emergency_team_description', icon: '🚨' },
      { button: 'ovace_button', description: 'ovace_description', icon: '🚫' },
    ],
  };

  const renderAcronymCard = (acronym, meaningKey) => (
    <div key={acronym} className="acronym-card">
      <div className="acronym-header">
        <div className="acronym-badge">{acronym}</div>
        <div className="acronym-meaning">{T(meaningKey)}</div>
      </div>
    </div>
  );

  const renderButtonCard = (buttonKey, descriptionKey, icon) => (
    <div key={buttonKey} className="button-card">
      <div className="button-header">
        <div className="button-icon">{icon}</div>
        <div className="button-title">{T(buttonKey)}</div>
      </div>
      <div className="button-description">{T(descriptionKey)}</div>
    </div>
  );

  const renderCategory = (title, items, renderFunction) => (
    <div key={title} className="category-section">
      <h3 className="category-title">{title}</h3>
      <div className={activeTab === 'acronyms' ? 'acronyms-grid' : 'buttonology-grid'}>
        {items.map((item) => 
          activeTab === 'acronyms' 
            ? renderFunction(item.acronym, item.meaning)
            : renderFunction(item.button, item.description, item.icon)
        )}
      </div>
    </div>
  );

  return (
    <div>
      <Header />
      <div className="help-container">
        <h1 className="help-title">{T('help_title')}</h1>
        <p className="help-description">{T('help_description')}</p>
        
        <div className="help-tabs">
          <button
            className={`help-tab ${activeTab === 'acronyms' ? 'active' : ''}`}
            onClick={() => setActiveTab('acronyms')}
          >
            {T('acronyms_section')}
          </button>
          <button
            className={`help-tab ${activeTab === 'buttonology' ? 'active' : ''}`}
            onClick={() => setActiveTab('buttonology')}
          >
            {T('buttonology_section')}
          </button>
        </div>
        
        <div className="help-content">
          {activeTab === 'acronyms' && (
            <>
              {renderCategory(
                language === 'ES' ? 'Ritmos Cardíacos' : 
                language === 'EN' ? 'Cardiac Rhythms' :
                language === 'IT' ? 'Ritmi Cardiaci' : 'Ritmos Cardíacos',
                acronymsData.rhythm,
                renderAcronymCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Bloqueos Cardíacos' : 
                language === 'EN' ? 'Heart Blocks' :
                language === 'IT' ? 'Blocchi Cardiaci' : 'Bloqueios Cardíacos',
                acronymsData.blocks,
                renderAcronymCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Procedimientos y Dispositivos' : 
                language === 'EN' ? 'Procedures and Devices' :
                language === 'IT' ? 'Procedure e Dispositivi' : 'Procedimentos e Dispositivos',
                acronymsData.procedures,
                renderAcronymCard
              )}
            </>
          )}
          
          {activeTab === 'buttonology' && (
            <>
              {renderCategory(
                language === 'ES' ? 'Botones Principales' : 
                language === 'EN' ? 'Main Buttons' :
                language === 'IT' ? 'Pulsanti Principali' : 'Botões Principais',
                buttonsData.main,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Ritmo y Evaluación' : 
                language === 'EN' ? 'Rhythm and Evaluation' :
                language === 'IT' ? 'Ritmo e Valutazione' : 'Ritmo e Avaliação',
                buttonsData.rhythm,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'RCP - Compresiones y Ventilaciones' : 
                language === 'EN' ? 'CPR - Compressions and Ventilations' :
                language === 'IT' ? 'RCP - Compressioni e Ventilazioni' : 'RCP - Compressões e Ventilações',
                buttonsData.cpr,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Tratamiento' : 
                language === 'EN' ? 'Treatment' :
                language === 'IT' ? 'Trattamento' : 'Tratamento',
                buttonsData.treatment,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Procedimientos' : 
                language === 'EN' ? 'Procedures' :
                language === 'IT' ? 'Procedure' : 'Procedimentos',
                buttonsData.airway,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Calidad RCP' : 
                language === 'EN' ? 'CPR Quality' :
                language === 'IT' ? 'Qualità RCP' : 'Qualidade RCP',
                buttonsData.quality,
                renderButtonCard
              )}
              
              {renderCategory(
                language === 'ES' ? 'Otros' : 
                language === 'EN' ? 'Others' :
                language === 'IT' ? 'Altri' : 'Outros',
                buttonsData.other,
                renderButtonCard
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Help; 